/**
 * Point entity → `RenderableEntity` adapter (DEFUSE S3 — TYPED).
 *
 * Typed counterpart of the legacy `pointToRenderable.ts`. Reads the
 * canonical {@link PointEntity} model fields (`location.{x,y,z}`) instead
 * of the `EntityPreview.properties` string bag (legacy read
 * `readNumber(entity.properties, "Position X / Y / Z")`), then builds the
 * SAME `SceneGeometry` + bounds and routes through the typed
 * `buildRenderableShell(entity, ctx, geometry, bounds)` overload.
 *
 * Goal: byte-identical output to the legacy adapter for the same entity
 * (S3 golden-parity). The PDMODE / PDSIZE bitset + size are still carried
 * on the geometry exactly as legacy so the renderer (PR-B
 * `pointMarkerMeshes.ts`) can pick the marker prototype without
 * re-parsing.
 *
 * Category B: standalone file, NOT wired into any render path; the legacy
 * adapter and the live pipeline are untouched.
 */

import type { PointEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { buildPointSceneGeometry } from "../sceneGeometryBuilders";

/** Typed adapter for the `Point` (AcDbPoint / DXF POINT) entity. */
export function pointFromEntity(
  entity: PointEntity,
  ctx: AdapterContext & { pdmode?: number; pdsize?: number },
): RenderableEntity {
  // Typed `location` carries an OPTIONAL z (drawingPointOptionalZSchema).
  // Legacy read "Position Z" through `readNumber`, which returns 0 when the
  // key is absent / non-finite — so `?? 0` here reproduces that default
  // exactly before the origin offset is applied.
  const positionRaw: Vec3 = {
    x: entity.location.x,
    y: entity.location.y,
    z: entity.location.z ?? 0,
  };

  // PDMODE / PDSIZE defaults are resolved inside the shared builder.
  const { geometry, bounds } = buildPointSceneGeometry(
    positionRaw,
    ctx.pdmode,
    ctx.pdsize,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
